/**
 * Authentication Service
 * Handles user authentication state and API calls
 */

const AUTH_API_BASE_URL = import.meta.env.VITE_AUTH_API_BASE_URL || 'http://localhost:5001';
const LOGIN_URL = import.meta.env.VITE_LOGIN_URL || 'http://localhost:8000/login.dev.html';

class AuthService {
    constructor() {
        this.baseUrl = AUTH_API_BASE_URL;
        this.loginUrl = LOGIN_URL;
        this.user = null;
        this.listeners = new Set();
    }

    /**
     * Check if user is authenticated
     * @returns {Promise<Object|null>} User object or null
     */
    async checkAuth() {
        try {
            const res = await fetch(`${this.baseUrl}/auth/me`, {
                credentials: 'include'
            });

            if (res.ok) {
                this.user = await res.json();
                this.notifyListeners();
                return this.user;
            } else {
                this.user = null;
                this.notifyListeners();
                return null;
            }
        } catch (err) {
            console.error('Auth check failed:', err);

            // Mock auth for local development only.
            // Safe: import.meta.env.DEV is a compile-time constant that Vite
            // replaces with `false` in production builds, so the entire block
            // is dead-code-eliminated and never ships to production.
            if (import.meta.env.DEV && import.meta.env.VITE_AUTH_DISABLED === 'true') {
                console.warn('[AuthService] Auth disabled — using mock user (dev only)');
                this.user = {
                    id: 'dev-user',
                    email: 'dev@cornercast.tv',
                    name: 'Dev User',
                    organizations: [
                        { id: 'org-dev', name: 'Acme Media', slug: 'acme-media', role: 'owner' },
                    ],
                };
                this.notifyListeners();
                return this.user;
            }

            this.user = null;
            this.notifyListeners();
            return null;
        }
    }

    /**
     * Get current user (cached)
     * @returns {Object|null} User object or null
     */
    getUser() {
        return this.user;
    }

    /**
     * Check if user is authenticated (sync)
     * @returns {boolean}
     */
    isAuthenticated() {
        return this.user !== null;
    }

    /**
     * Logout user
     * @returns {Promise<void>}
     */
    async logout() {
        try {
            await fetch(`${this.baseUrl}/auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (err) {
            console.error('Logout failed:', err);
        }

        this.user = null;
        this.notifyListeners();
        this.redirectToLogin();
    }

    /**
     * Redirect to login page
     */
    redirectToLogin() {
        window.location.href = this.loginUrl;
    }

    /**
     * Handle 401 unauthorized response
     */
    handleUnauthorized() {
        this.user = null;
        this.notifyListeners();
        this.redirectToLogin();
    }

    /**
     * Subscribe to auth state changes
     * @param {Function} callback
     * @returns {Function} Unsubscribe function
     */
    subscribe(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    /**
     * Notify all listeners of auth state change
     */
    notifyListeners() {
        this.listeners.forEach(callback => callback(this.user));
    }

    /**
     * Get auth headers for API requests
     * Note: We use cookies, so this is mainly for compatibility
     * @returns {Object}
     */
    getAuthHeaders() {
        return {};
    }
}

// Create singleton instance
export const authService = new AuthService();

// Export for use in components
export default authService;

// Utility function to create a fetch wrapper with auth handling
export function createAuthenticatedFetch() {
    return async (url, options = {}) => {
        const response = await fetch(url, {
            ...options,
            credentials: 'include'
        });

        if (response.status === 401) {
            authService.handleUnauthorized();
            throw new Error('Unauthorized');
        }

        return response;
    };
}
